import type { ReactNode } from 'react';
import type {
  CadCategory,
  IntakeSubType,
  IntakeDetails,
  FurnitureTableDetails,
  FurnitureChairDetails,
  FurnitureShelfDetails,
  FurnitureBedDetails,
  FurnitureDeskDetails,
  FurnitureCabinetDetails,
  MechanicalBracketDetails,
  MechanicalHousingDetails,
  MechanicalGearDetails,
  MechanicalShaftDetails,
  MechanicalFastenerPartDetails,
  MechanicalFixtureDetails,
  ArchitectureBuildingDetails,
  ArchitectureInteriorRoomDetails,
  ArchitectureSiteDetails,
  ArchitectureFacadeDetailDetails,
  PrintingDecorativeDetails,
  PrintingFunctionalDetails,
  PrintingReplacementDetails,
  PrintingMiniatureDetails,
  PrintingEnclosureDetails,
} from '@shared/types';
import { ChipSelect } from '@/components/intake/fields/ChipSelect';
import { NumberWithUnit } from '@/components/intake/fields/NumberWithUnit';
import { ToggleChip } from '@/components/intake/fields/ToggleChip';
import { useTranslation } from 'react-i18next';

// ─── Shared helper ────────────────────────────────────────────────────────────

function Field({ label, children }: { label: string; children: ReactNode }) {
  const { t } = useTranslation('intake');
  return (
    <div>
      <label className="text-[10px] text-synergy-text-tertiary">
        {t(`fieldLabels.${label}`, { defaultValue: label })}
      </label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

// ─── Top-level dispatcher ─────────────────────────────────────────────────────

export function DetailsSection({
  category,
  subType,
  value,
  onChange,
}: {
  category: CadCategory;
  subType: IntakeSubType;
  value: IntakeDetails | undefined;
  onChange: (next: IntakeDetails) => void;
}) {
  // ── Furniture ──
  if (category === 'furniture' && subType === 'table') {
    return <TableForm value={value as FurnitureTableDetails | undefined} onChange={onChange} />;
  }
  if (category === 'furniture' && subType === 'chair') {
    return <ChairForm value={value as FurnitureChairDetails | undefined} onChange={onChange} />;
  }
  if (category === 'furniture' && subType === 'shelf') {
    return <ShelfForm value={value as FurnitureShelfDetails | undefined} onChange={onChange} />;
  }
  if (category === 'furniture' && subType === 'bed') {
    return <BedForm value={value as FurnitureBedDetails | undefined} onChange={onChange} />;
  }
  if (category === 'furniture' && subType === 'desk') {
    return <DeskForm value={value as FurnitureDeskDetails | undefined} onChange={onChange} />;
  }
  if (category === 'furniture' && subType === 'cabinet') {
    return <CabinetForm value={value as FurnitureCabinetDetails | undefined} onChange={onChange} />;
  }
  // ── Mechanical ──
  if (category === 'mechanical' && subType === 'bracket') {
    return <BracketForm value={value as MechanicalBracketDetails | undefined} onChange={onChange} />;
  }
  if (category === 'mechanical' && subType === 'housing') {
    return <HousingForm value={value as MechanicalHousingDetails | undefined} onChange={onChange} />;
  }
  if (category === 'mechanical' && subType === 'gear') {
    return <GearForm value={value as MechanicalGearDetails | undefined} onChange={onChange} />;
  }
  if (category === 'mechanical' && subType === 'shaft') {
    return <ShaftForm value={value as MechanicalShaftDetails | undefined} onChange={onChange} />;
  }
  if (category === 'mechanical' && subType === 'fastener_part') {
    return <FastenerPartForm value={value as MechanicalFastenerPartDetails | undefined} onChange={onChange} />;
  }
  if (category === 'mechanical' && subType === 'fixture') {
    return <FixtureForm value={value as MechanicalFixtureDetails | undefined} onChange={onChange} />;
  }
  // ── Architecture ──
  if (category === 'architecture' && subType === 'building') {
    return <BuildingForm value={value as ArchitectureBuildingDetails | undefined} onChange={onChange} />;
  }
  if (category === 'architecture' && subType === 'interior_room') {
    return <InteriorRoomForm value={value as ArchitectureInteriorRoomDetails | undefined} onChange={onChange} />;
  }
  if (category === 'architecture' && subType === 'site') {
    return <SiteForm value={value as ArchitectureSiteDetails | undefined} onChange={onChange} />;
  }
  if (category === 'architecture' && subType === 'facade_detail') {
    return <FacadeDetailForm value={value as ArchitectureFacadeDetailDetails | undefined} onChange={onChange} />;
  }
  // ── Printing ──
  if (category === 'printing' && subType === 'decorative') {
    return <DecorativeForm value={value as PrintingDecorativeDetails | undefined} onChange={onChange} />;
  }
  if (category === 'printing' && subType === 'functional') {
    return <FunctionalForm value={value as PrintingFunctionalDetails | undefined} onChange={onChange} />;
  }
  if (category === 'printing' && subType === 'replacement') {
    return <ReplacementForm value={value as PrintingReplacementDetails | undefined} onChange={onChange} />;
  }
  if (category === 'printing' && subType === 'miniature') {
    return <MiniatureForm value={value as PrintingMiniatureDetails | undefined} onChange={onChange} />;
  }
  if (category === 'printing' && subType === 'enclosure') {
    return <EnclosureForm value={value as PrintingEnclosureDetails | undefined} onChange={onChange} />;
  }

  return null;
}

// ─── Furniture variants ───────────────────────────────────────────────────────

function TableForm({
  value,
  onChange,
}: {
  value: FurnitureTableDetails | undefined;
  onChange: (next: FurnitureTableDetails) => void;
}) {
  const v: FurnitureTableDetails = value ?? {
    category: 'furniture',
    subType: 'table',
    numSeats: 6,
    shape: 'rectangular',
    legStyle: 'straight',
    joineryType: 'mortise_tenon',
    finish: 'oil',
    hasStretcher: false,
    hasApron: true,
  };
  const update = (next: Partial<FurnitureTableDetails>) => onChange({ ...v, ...next });

  return (
    <div className="flex flex-col gap-3">
      <Field label="座席数">
        <ChipSelect
          options={[
            { value: 2, label: '2' },
            { value: 4, label: '4' },
            { value: 6, label: '6' },
            { value: 8, label: '8' },
            { value: 10, label: '10' },
            { value: 12, label: '12' },
          ]}
          value={v.numSeats}
          onChange={(numSeats) => update({ numSeats })}
        />
      </Field>
      <Field label="形状">
        <ChipSelect
          options={[
            { value: 'rectangular', label: '長方形' },
            { value: 'round', label: '円' },
            { value: 'oval', label: '楕円' },
            { value: 'square', label: '正方形' },
          ]}
          value={v.shape}
          onChange={(shape) => update({ shape })}
        />
      </Field>
      <Field label="脚スタイル">
        <ChipSelect
          options={[
            { value: 'straight', label: 'ストレート' },
            { value: 'tapered', label: 'テーパー' },
            { value: 'turned', label: '丸棒' },
            { value: 'trestle', label: 'トレッスル' },
            { value: 'pedestal', label: '台座' },
          ]}
          value={v.legStyle}
          onChange={(legStyle) => update({ legStyle })}
        />
      </Field>
      <Field label="接合方法">
        <ChipSelect
          options={[
            { value: 'mortise_tenon', label: 'ほぞ継ぎ' },
            { value: 'dowel', label: 'ダボ' },
            { value: 'biscuit', label: 'ビスケット' },
            { value: 'pocket_screw', label: 'ポケットスクリュー' },
            { value: 'metal_bracket', label: '金属ブラケット' },
          ]}
          value={v.joineryType}
          onChange={(joineryType) => update({ joineryType })}
        />
      </Field>
      <Field label="仕上げ">
        <ChipSelect
          options={[
            { value: 'oil', label: 'オイル' },
            { value: 'lacquer', label: 'ラッカー' },
            { value: 'paint', label: '塗装' },
            { value: 'wax', label: 'ワックス' },
            { value: 'unfinished', label: '未仕上げ' },
          ]}
          value={v.finish}
          onChange={(finish) => update({ finish })}
        />
      </Field>
      <div className="flex flex-wrap gap-2 pt-1">
        <ToggleChip value={v.hasStretcher} onChange={(hasStretcher) => update({ hasStretcher })} label="幕板" />
        <ToggleChip value={v.hasApron} onChange={(hasApron) => update({ hasApron })} label="エプロン" />
      </div>
    </div>
  );
}

function ChairForm({
  value,
  onChange,
}: {
  value: FurnitureChairDetails | undefined;
  onChange: (next: FurnitureChairDetails) => void;
}) {
  const v: FurnitureChairDetails = value ?? {
    category: 'furniture',
    subType: 'chair',
    chairType: 'dining',
    backStyle: 'slat',
    hasArmrests: false,
    legStyle: 'straight',
    joineryType: 'mortise_tenon',
    finish: 'oil',
  };
  const update = (next: Partial<FurnitureChairDetails>) => onChange({ ...v, ...next });

  return (
    <div className="flex flex-col gap-3">
      <Field label="椅子タイプ">
        <ChipSelect
          options={[
            { value: 'dining', label: 'ダイニング' },
            { value: 'lounge', label: 'ラウンジ' },
            { value: 'office', label: 'オフィス' },
            { value: 'stool', label: 'スツール' },
            { value: 'rocking', label: 'ロッキング' },
          ]}
          value={v.chairType}
          onChange={(chairType) => update({ chairType })}
        />
      </Field>
      <Field label="背もたれスタイル">
        <ChipSelect
          options={[
            { value: 'spindle', label: 'スピンドル' },
            { value: 'slat', label: 'スラット' },
            { value: 'panel', label: 'パネル' },
            { value: 'upholstered', label: '張地' },
            { value: 'mesh', label: 'メッシュ' },
          ]}
          value={v.backStyle}
          onChange={(backStyle) => update({ backStyle })}
        />
      </Field>
      <Field label="脚スタイル">
        <ChipSelect
          options={[
            { value: 'straight', label: 'ストレート' },
            { value: 'tapered', label: 'テーパー' },
            { value: 'splayed', label: '開き脚' },
            { value: 'cabriole', label: 'カブリオレ' },
          ]}
          value={v.legStyle}
          onChange={(legStyle) => update({ legStyle })}
        />
      </Field>
      <Field label="接合方法">
        <ChipSelect
          options={[
            { value: 'mortise_tenon', label: 'ほぞ継ぎ' },
            { value: 'dowel', label: 'ダボ' },
            { value: 'screw', label: 'ネジ' },
            { value: 'mixed', label: '混在' },
          ]}
          value={v.joineryType}
          onChange={(joineryType) => update({ joineryType })}
        />
      </Field>
      <Field label="仕上げ">
        <ChipSelect
          options={[
            { value: 'oil', label: 'オイル' },
            { value: 'lacquer', label: 'ラッカー' },
            { value: 'paint', label: '塗装' },
            { value: 'wax', label: 'ワックス' },
            { value: 'unfinished', label: '未仕上げ' },
          ]}
          value={v.finish}
          onChange={(finish) => update({ finish })}
        />
      </Field>
      <div className="flex flex-wrap gap-2 pt-1">
        <ToggleChip value={v.hasArmrests} onChange={(hasArmrests) => update({ hasArmrests })} label="肘掛け" />
      </div>
    </div>
  );
}

function ShelfForm({
  value,
  onChange,
}: {
  value: FurnitureShelfDetails | undefined;
  onChange: (next: FurnitureShelfDetails) => void;
}) {
  const v: FurnitureShelfDetails = value ?? {
    category: 'furniture',
    subType: 'shelf',
    shelfType: 'bookcase',
    shelfCount: 4,
    adjustableShelves: true,
    hasBack: true,
    joineryType: 'dado',
    finish: 'oil',
  };
  const update = (next: Partial<FurnitureShelfDetails>) => onChange({ ...v, ...next });

  return (
    <div className="flex flex-col gap-3">
      <Field label="棚タイプ">
        <ChipSelect
          options={[
            { value: 'bookcase', label: 'ブックケース' },
            { value: 'wall_mounted', label: '壁掛け' },
            { value: 'cube', label: 'キューブ' },
            { value: 'ladder', label: 'ラダー' },
            { value: 'corner', label: 'コーナー' },
          ]}
          value={v.shelfType}
          onChange={(shelfType) => update({ shelfType })}
        />
      </Field>
      <Field label="段数">
        <NumberWithUnit value={v.shelfCount} onChange={(shelfCount) => update({ shelfCount: shelfCount ?? 4 })} unit="段" min={1} max={20} />
      </Field>
      <Field label="接合方法">
        <ChipSelect
          options={[
            { value: 'dado', label: '溝加工' },
            { value: 'dowel', label: 'ダボ' },
            { value: 'screw', label: 'ネジ' },
            { value: 'biscuit', label: 'ビスケット' },
            { value: 'cam_lock', label: 'カムロック' },
          ]}
          value={v.joineryType}
          onChange={(joineryType) => update({ joineryType })}
        />
      </Field>
      <Field label="仕上げ">
        <ChipSelect
          options={[
            { value: 'oil', label: 'オイル' },
            { value: 'lacquer', label: 'ラッカー' },
            { value: 'paint', label: '塗装' },
            { value: 'wax', label: 'ワックス' },
            { value: 'unfinished', label: '未仕上げ' },
          ]}
          value={v.finish}
          onChange={(finish) => update({ finish })}
        />
      </Field>
      <div className="flex flex-wrap gap-2 pt-1">
        <ToggleChip value={v.adjustableShelves} onChange={(adjustableShelves) => update({ adjustableShelves })} label="可動棚" />
        <ToggleChip value={v.hasBack} onChange={(hasBack) => update({ hasBack })} label="背板あり" />
      </div>
    </div>
  );
}

function BedForm({
  value,
  onChange,
}: {
  value: FurnitureBedDetails | undefined;
  onChange: (next: FurnitureBedDetails) => void;
}) {
  const v: FurnitureBedDetails = value ?? {
    category: 'furniture',
    subType: 'bed',
    bedSize: 'double',
    hasHeadboard: true,
    hasFootboard: false,
    hasUnderStorage: false,
    slatCount: 14,
    joineryType: 'bed_bolt',
    finish: 'oil',
  };
  const update = (next: Partial<FurnitureBedDetails>) => onChange({ ...v, ...next });

  return (
    <div className="flex flex-col gap-3">
      <Field label="ベッドサイズ">
        <ChipSelect
          options={[
            { value: 'single', label: 'シングル' },
            { value: 'semi_double', label: 'セミダブル' },
            { value: 'double', label: 'ダブル' },
            { value: 'queen', label: 'クイーン' },
            { value: 'king', label: 'キング' },
          ]}
          value={v.bedSize}
          onChange={(bedSize) => update({ bedSize })}
        />
      </Field>
      <Field label="スラット数">
        <NumberWithUnit value={v.slatCount} onChange={(slatCount) => update({ slatCount: slatCount ?? 14 })} unit="本" min={1} max={30} />
      </Field>
      <Field label="接合方法">
        <ChipSelect
          options={[
            { value: 'mortise_tenon', label: 'ほぞ継ぎ' },
            { value: 'bed_bolt', label: 'ベッドボルト' },
            { value: 'cam_lock', label: 'カムロック' },
            { value: 'screw', label: 'ネジ' },
          ]}
          value={v.joineryType}
          onChange={(joineryType) => update({ joineryType })}
        />
      </Field>
      <Field label="仕上げ">
        <ChipSelect
          options={[
            { value: 'oil', label: 'オイル' },
            { value: 'lacquer', label: 'ラッカー' },
            { value: 'paint', label: '塗装' },
            { value: 'wax', label: 'ワックス' },
            { value: 'unfinished', label: '未仕上げ' },
          ]}
          value={v.finish}
          onChange={(finish) => update({ finish })}
        />
      </Field>
      <div className="flex flex-wrap gap-2 pt-1">
        <ToggleChip value={v.hasHeadboard} onChange={(hasHeadboard) => update({ hasHeadboard })} label="ヘッドボード" />
        <ToggleChip value={v.hasFootboard} onChange={(hasFootboard) => update({ hasFootboard })} label="フットボード" />
        <ToggleChip value={v.hasUnderStorage} onChange={(hasUnderStorage) => update({ hasUnderStorage })} label="収納付き" />
      </div>
    </div>
  );
}

function DeskForm({
  value,
  onChange,
}: {
  value: FurnitureDeskDetails | undefined;
  onChange: (next: FurnitureDeskDetails) => void;
}) {
  const v: FurnitureDeskDetails = value ?? {
    category: 'furniture',
    subType: 'desk',
    deskType: 'simple',
    drawerCount: 0,
    hasCableManagement: false,
    legStyle: 'straight',
    joineryType: 'mortise_tenon',
    finish: 'oil',
  };
  const update = (next: Partial<FurnitureDeskDetails>) => onChange({ ...v, ...next });

  return (
    <div className="flex flex-col gap-3">
      <Field label="デスクタイプ">
        <ChipSelect
          options={[
            { value: 'simple', label: 'シンプル' },
            { value: 'pedestal_single', label: '片袖' },
            { value: 'pedestal_double', label: '両袖' },
            { value: 'l_shape', label: 'L字' },
            { value: 'standing', label: 'スタンディング' },
          ]}
          value={v.deskType}
          onChange={(deskType) => update({ deskType })}
        />
      </Field>
      <Field label="引き出し数">
        <NumberWithUnit value={v.drawerCount} onChange={(drawerCount) => update({ drawerCount: drawerCount ?? 0 })} unit="個" min={0} max={10} />
      </Field>
      <Field label="脚スタイル">
        <ChipSelect
          options={[
            { value: 'straight', label: 'ストレート' },
            { value: 'tapered', label: 'テーパー' },
            { value: 'panel', label: 'パネル' },
            { value: 'metal', label: '金属' },
          ]}
          value={v.legStyle}
          onChange={(legStyle) => update({ legStyle })}
        />
      </Field>
      <Field label="接合方法">
        <ChipSelect
          options={[
            { value: 'mortise_tenon', label: 'ほぞ継ぎ' },
            { value: 'dowel', label: 'ダボ' },
            { value: 'pocket_screw', label: 'ポケットスクリュー' },
            { value: 'cam_lock', label: 'カムロック' },
          ]}
          value={v.joineryType}
          onChange={(joineryType) => update({ joineryType })}
        />
      </Field>
      <Field label="仕上げ">
        <ChipSelect
          options={[
            { value: 'oil', label: 'オイル' },
            { value: 'lacquer', label: 'ラッカー' },
            { value: 'paint', label: '塗装' },
            { value: 'wax', label: 'ワックス' },
            { value: 'unfinished', label: '未仕上げ' },
          ]}
          value={v.finish}
          onChange={(finish) => update({ finish })}
        />
      </Field>
      <div className="flex flex-wrap gap-2 pt-1">
        <ToggleChip value={v.hasCableManagement} onChange={(hasCableManagement) => update({ hasCableManagement })} label="ケーブル管理" />
      </div>
    </div>
  );
}

function CabinetForm({
  value,
  onChange,
}: {
  value: FurnitureCabinetDetails | undefined;
  onChange: (next: FurnitureCabinetDetails) => void;
}) {
  const v: FurnitureCabinetDetails = value ?? {
    category: 'furniture',
    subType: 'cabinet',
    cabinetType: 'base',
    doorStyle: 'flat_panel',
    doorCount: 2,
    drawerCount: 1,
    shelvesInside: 1,
    hardwareStyle: 'concealed_hinge',
    joineryType: 'dado',
    finish: 'paint',
  };
  const update = (next: Partial<FurnitureCabinetDetails>) => onChange({ ...v, ...next });

  return (
    <div className="flex flex-col gap-3">
      <Field label="キャビネットタイプ">
        <ChipSelect
          options={[
            { value: 'base', label: 'ベース' },
            { value: 'wall', label: '壁掛け' },
            { value: 'tall', label: 'トール' },
            { value: 'corner', label: 'コーナー' },
            { value: 'island', label: 'アイランド' },
          ]}
          value={v.cabinetType}
          onChange={(cabinetType) => update({ cabinetType })}
        />
      </Field>
      <Field label="扉スタイル">
        <ChipSelect
          options={[
            { value: 'flat_panel', label: 'フラット' },
            { value: 'shaker', label: 'シェーカー' },
            { value: 'raised_panel', label: '框組み' },
            { value: 'glass', label: 'ガラス' },
            { value: 'sliding', label: '引き戸' },
            { value: 'none', label: 'なし' },
          ]}
          value={v.doorStyle}
          onChange={(doorStyle) => update({ doorStyle })}
        />
      </Field>
      <Field label="扉数">
        <NumberWithUnit value={v.doorCount} onChange={(doorCount) => update({ doorCount: doorCount ?? 2 })} unit="枚" min={0} max={12} />
      </Field>
      <Field label="引き出し数">
        <NumberWithUnit value={v.drawerCount} onChange={(drawerCount) => update({ drawerCount: drawerCount ?? 1 })} unit="個" min={0} max={10} />
      </Field>
      <Field label="内部棚数">
        <NumberWithUnit value={v.shelvesInside} onChange={(shelvesInside) => update({ shelvesInside: shelvesInside ?? 1 })} unit="段" min={0} max={10} />
      </Field>
      <Field label="丁番スタイル">
        <ChipSelect
          options={[
            { value: 'concealed_hinge', label: '隠し蝶番' },
            { value: 'butt_hinge', label: '平丁番' },
            { value: 'piano_hinge', label: 'ピアノ蝶番' },
            { value: 'cup_hinge', label: 'カップ蝶番' },
          ]}
          value={v.hardwareStyle}
          onChange={(hardwareStyle) => update({ hardwareStyle })}
        />
      </Field>
      <Field label="接合方法">
        <ChipSelect
          options={[
            { value: 'dado', label: '溝加工' },
            { value: 'dowel', label: 'ダボ' },
            { value: 'cam_lock', label: 'カムロック' },
            { value: 'pocket_screw', label: 'ポケットスクリュー' },
          ]}
          value={v.joineryType}
          onChange={(joineryType) => update({ joineryType })}
        />
      </Field>
      <Field label="仕上げ">
        <ChipSelect
          options={[
            { value: 'oil', label: 'オイル' },
            { value: 'lacquer', label: 'ラッカー' },
            { value: 'paint', label: '塗装' },
            { value: 'melamine', label: 'メラミン' },
            { value: 'unfinished', label: '未仕上げ' },
          ]}
          value={v.finish}
          onChange={(finish) => update({ finish })}
        />
      </Field>
    </div>
  );
}

// ─── Mechanical variants ──────────────────────────────────────────────────────

function BracketForm({
  value,
  onChange,
}: {
  value: MechanicalBracketDetails | undefined;
  onChange: (next: MechanicalBracketDetails) => void;
}) {
  const v: MechanicalBracketDetails = value ?? {
    category: 'mechanical',
    subType: 'bracket',
    bracketType: 'L',
    mountingPattern: 'corner',
    holeCount: 4,
    holeSize: 'M5',
    holeFinish: 'through',
    loadDirection: 'shear',
    surfaceFinish: 'as_machined',
  };
  const update = (next: Partial<MechanicalBracketDetails>) => onChange({ ...v, ...next });

  return (
    <div className="flex flex-col gap-3">
      <Field label="ブラケットタイプ">
        <ChipSelect
          options={[
            { value: 'L', label: 'L型' },
            { value: 'T', label: 'T型' },
            { value: 'flat', label: 'フラット' },
            { value: 'angle', label: 'アングル' },
            { value: 'gusset', label: 'ガセット' },
          ]}
          value={v.bracketType}
          onChange={(bracketType) => update({ bracketType })}
        />
      </Field>
      <Field label="取付パターン">
        <ChipSelect
          options={[
            { value: 'corner', label: 'コーナー' },
            { value: 'flat', label: 'フラット' },
            { value: 'angled', label: 'アングル' },
            { value: 'offset', label: 'オフセット' },
          ]}
          value={v.mountingPattern}
          onChange={(mountingPattern) => update({ mountingPattern })}
        />
      </Field>
      <Field label="穴数">
        <NumberWithUnit value={v.holeCount} onChange={(holeCount) => update({ holeCount: holeCount ?? 4 })} unit="個" min={1} max={20} />
      </Field>
      <Field label="穴サイズ">
        <ChipSelect
          options={[
            { value: 'M3', label: 'M3' },
            { value: 'M4', label: 'M4' },
            { value: 'M5', label: 'M5' },
            { value: 'M6', label: 'M6' },
            { value: 'M8', label: 'M8' },
            { value: 'M10', label: 'M10' },
          ]}
          value={v.holeSize}
          onChange={(holeSize) => update({ holeSize })}
        />
      </Field>
      <Field label="穴仕上げ">
        <ChipSelect
          options={[
            { value: 'through', label: '貫通' },
            { value: 'countersunk', label: '皿もみ' },
            { value: 'counterbored', label: 'ざぐり' },
            { value: 'threaded', label: 'ネジ穴' },
          ]}
          value={v.holeFinish}
          onChange={(holeFinish) => update({ holeFinish })}
        />
      </Field>
      <Field label="荷重方向">
        <ChipSelect
          options={[
            { value: 'shear', label: 'せん断' },
            { value: 'tension', label: '引張' },
            { value: 'cantilever', label: '片持ち' },
            { value: 'mixed', label: '複合' },
          ]}
          value={v.loadDirection}
          onChange={(loadDirection) => update({ loadDirection })}
        />
      </Field>
      <Field label="表面処理">
        <ChipSelect
          options={[
            { value: 'as_machined', label: '切削のまま' },
            { value: 'powder_coat', label: '粉体塗装' },
            { value: 'anodized', label: 'アルマイト' },
            { value: 'plated', label: 'めっき' },
            { value: 'painted', label: '塗装' },
          ]}
          value={v.surfaceFinish}
          onChange={(surfaceFinish) => update({ surfaceFinish })}
        />
      </Field>
    </div>
  );
}

function HousingForm({
  value,
  onChange,
}: {
  value: MechanicalHousingDetails | undefined;
  onChange: (next: MechanicalHousingDetails) => void;
}) {
  const v: MechanicalHousingDetails = value ?? {
    category: 'mechanical',
    subType: 'housing',
    housingType: 'enclosure',
    closure: 'lid_screwed',
    ipRating: 'none',
    internalFeatures: [],
    fastenerSize: 'M3',
    surfaceFinish: 'as_machined',
  };
  const update = (next: Partial<MechanicalHousingDetails>) => onChange({ ...v, ...next });

  type InternalFeature = MechanicalHousingDetails['internalFeatures'][number];
  const has = (k: InternalFeature) => v.internalFeatures.includes(k);
  const toggle = (k: InternalFeature) => {
    const next = has(k)
      ? v.internalFeatures.filter((x) => x !== k)
      : [...v.internalFeatures, k];
    update({ internalFeatures: next });
  };

  return (
    <div className="flex flex-col gap-3">
      <Field label="筐体タイプ">
        <ChipSelect
          options={[
            { value: 'enclosure', label: 'エンクロージャ' },
            { value: 'gearbox', label: 'ギアボックス' },
            { value: 'motor', label: 'モーター' },
            { value: 'pump', label: 'ポンプ' },
            { value: 'electronics_box', label: '電装ボックス' },
          ]}
          value={v.housingType}
          onChange={(housingType) => update({ housingType })}
        />
      </Field>
      <Field label="蓋の固定">
        <ChipSelect
          options={[
            { value: 'lid_screwed', label: 'ネジ止め蓋' },
            { value: 'lid_snap_fit', label: 'スナップ蓋' },
            { value: 'split_clamshell', label: '二つ割り' },
            { value: 'access_panel', label: 'アクセスパネル' },
          ]}
          value={v.closure}
          onChange={(closure) => update({ closure })}
        />
      </Field>
      <Field label="IP等級">
        <ChipSelect
          options={[
            { value: 'none', label: 'なし' },
            { value: 'IP54', label: 'IP54' },
            { value: 'IP65', label: 'IP65' },
            { value: 'IP67', label: 'IP67' },
          ]}
          value={v.ipRating}
          onChange={(ipRating) => update({ ipRating })}
        />
      </Field>
      <Field label="内部フィーチャー">
        <div className="flex flex-wrap gap-2">
          <ToggleChip value={has('mounting_bosses')} onChange={() => toggle('mounting_bosses')} label="取付ボス" />
          <ToggleChip value={has('cable_glands')} onChange={() => toggle('cable_glands')} label="ケーブルグランド" />
          <ToggleChip value={has('vent')} onChange={() => toggle('vent')} label="通気口" />
          <ToggleChip value={has('cooling_fins')} onChange={() => toggle('cooling_fins')} label="冷却フィン" />
        </div>
      </Field>
      <Field label="ファスナーサイズ">
        <ChipSelect
          options={[
            { value: 'M2', label: 'M2' },
            { value: 'M2.5', label: 'M2.5' },
            { value: 'M3', label: 'M3' },
            { value: 'M4', label: 'M4' },
            { value: 'M5', label: 'M5' },
          ]}
          value={v.fastenerSize}
          onChange={(fastenerSize) => update({ fastenerSize })}
        />
      </Field>
      <Field label="表面処理">
        <ChipSelect
          options={[
            { value: 'as_machined', label: '切削のまま' },
            { value: 'powder_coat', label: '粉体塗装' },
            { value: 'anodized', label: 'アルマイト' },
            { value: 'painted', label: '塗装' },
          ]}
          value={v.surfaceFinish}
          onChange={(surfaceFinish) => update({ surfaceFinish })}
        />
      </Field>
    </div>
  );
}

function GearForm({
  value,
  onChange,
}: {
  value: MechanicalGearDetails | undefined;
  onChange: (next: MechanicalGearDetails) => void;
}) {
  const v: MechanicalGearDetails = value ?? {
    category: 'mechanical',
    subType: 'gear',
    gearType: 'spur',
    module: 2,
    toothCount: 20,
    pressureAngle: 20,
    faceWidth: 20,
    boreDiameter: 10,
    hubStyle: 'plain',
  };
  const update = (next: Partial<MechanicalGearDetails>) => onChange({ ...v, ...next });

  return (
    <div className="flex flex-col gap-3">
      <Field label="歯車タイプ">
        <ChipSelect
          options={[
            { value: 'spur', label: '平歯車' },
            { value: 'helical', label: 'はすば歯車' },
            { value: 'bevel', label: 'かさ歯車' },
            { value: 'worm', label: 'ウォーム' },
            { value: 'rack', label: 'ラック' },
          ]}
          value={v.gearType}
          onChange={(gearType) => update({ gearType })}
        />
      </Field>
      <Field label="モジュール">
        <NumberWithUnit value={v.module} onChange={(module) => update({ module: module ?? 2 })} unit="" min={0.5} max={20} step={0.5} />
      </Field>
      <Field label="歯数">
        <NumberWithUnit value={v.toothCount} onChange={(toothCount) => update({ toothCount: toothCount ?? 20 })} unit="枚" min={4} max={200} />
      </Field>
      <Field label="圧力角">
        <ChipSelect
          options={[
            { value: 14.5, label: '14.5°' },
            { value: 20, label: '20°' },
            { value: 25, label: '25°' },
          ]}
          value={v.pressureAngle}
          onChange={(pressureAngle) => update({ pressureAngle })}
        />
      </Field>
      <Field label="歯幅 (mm)">
        <NumberWithUnit value={v.faceWidth} onChange={(faceWidth) => update({ faceWidth: faceWidth ?? 20 })} unit="mm" min={1} max={200} />
      </Field>
      <Field label="軸穴径 (mm)">
        <NumberWithUnit value={v.boreDiameter} onChange={(boreDiameter) => update({ boreDiameter: boreDiameter ?? 10 })} unit="mm" min={1} max={100} />
      </Field>
      <Field label="ハブスタイル">
        <ChipSelect
          options={[
            { value: 'none', label: 'なし' },
            { value: 'plain', label: 'プレーン' },
            { value: 'set_screw', label: 'セットスクリュー' },
            { value: 'keyed', label: 'キー溝' },
            { value: 'press_fit', label: '圧入' },
          ]}
          value={v.hubStyle}
          onChange={(hubStyle) => update({ hubStyle })}
        />
      </Field>
    </div>
  );
}

function ShaftForm({
  value,
  onChange,
}: {
  value: MechanicalShaftDetails | undefined;
  onChange: (next: MechanicalShaftDetails) => void;
}) {
  const v: MechanicalShaftDetails = value ?? {
    category: 'mechanical',
    subType: 'shaft',
    shaftType: 'plain',
    diameter: 20,
    keywayPresent: false,
    bearingFit: 'sliding',
    endStyle: 'chamfered',
    surfaceFinish: 'as_machined',
  };
  const update = (next: Partial<MechanicalShaftDetails>) => onChange({ ...v, ...next });

  return (
    <div className="flex flex-col gap-3">
      <Field label="シャフトタイプ">
        <ChipSelect
          options={[
            { value: 'plain', label: 'プレーン' },
            { value: 'stepped', label: '段付き' },
            { value: 'splined', label: 'スプライン' },
            { value: 'threaded', label: 'ネジ' },
            { value: 'keyed', label: 'キー溝' },
          ]}
          value={v.shaftType}
          onChange={(shaftType) => update({ shaftType })}
        />
      </Field>
      <Field label="直径 (mm)">
        <NumberWithUnit value={v.diameter} onChange={(diameter) => update({ diameter: diameter ?? 20 })} unit="mm" min={1} max={500} />
      </Field>
      <Field label="軸受けはめあい">
        <ChipSelect
          options={[
            { value: 'none', label: 'なし' },
            { value: 'press', label: '圧入' },
            { value: 'sliding', label: 'すべり' },
            { value: 'snug', label: 'スナッグ' },
            { value: 'transition', label: '中間' },
          ]}
          value={v.bearingFit}
          onChange={(bearingFit) => update({ bearingFit })}
        />
      </Field>
      <Field label="端部処理">
        <ChipSelect
          options={[
            { value: 'square', label: '直角' },
            { value: 'chamfered', label: '面取り' },
            { value: 'tapered', label: 'テーパー' },
            { value: 'threaded', label: 'ネジ' },
            { value: 'splined', label: 'スプライン' },
          ]}
          value={v.endStyle}
          onChange={(endStyle) => update({ endStyle })}
        />
      </Field>
      <Field label="表面処理">
        <ChipSelect
          options={[
            { value: 'as_machined', label: '切削のまま' },
            { value: 'ground', label: '研削' },
            { value: 'polished', label: '研磨' },
            { value: 'plated', label: 'めっき' },
          ]}
          value={v.surfaceFinish}
          onChange={(surfaceFinish) => update({ surfaceFinish })}
        />
      </Field>
      <div className="flex flex-wrap gap-2 pt-1">
        <ToggleChip value={v.keywayPresent} onChange={(keywayPresent) => update({ keywayPresent })} label="キー溝あり" />
      </div>
    </div>
  );
}

function FastenerPartForm({
  value,
  onChange,
}: {
  value: MechanicalFastenerPartDetails | undefined;
  onChange: (next: MechanicalFastenerPartDetails) => void;
}) {
  const v: MechanicalFastenerPartDetails = value ?? {
    category: 'mechanical',
    subType: 'fastener_part',
    fastenerType: 'bolt',
    size: 'M6',
    length: 20,
    head: 'hex',
    thread: 'full',
    finish: 'plain',
  };
  const update = (next: Partial<MechanicalFastenerPartDetails>) => onChange({ ...v, ...next });

  return (
    <div className="flex flex-col gap-3">
      <Field label="ファスナータイプ">
        <ChipSelect
          options={[
            { value: 'screw', label: 'ネジ' },
            { value: 'bolt', label: 'ボルト' },
            { value: 'nut', label: 'ナット' },
            { value: 'washer', label: 'ワッシャー' },
            { value: 'stud', label: 'スタッド' },
            { value: 'rivet', label: 'リベット' },
          ]}
          value={v.fastenerType}
          onChange={(fastenerType) => update({ fastenerType })}
        />
      </Field>
      <Field label="サイズ">
        <ChipSelect
          options={[
            { value: 'M2', label: 'M2' },
            { value: 'M3', label: 'M3' },
            { value: 'M4', label: 'M4' },
            { value: 'M5', label: 'M5' },
            { value: 'M6', label: 'M6' },
            { value: 'M8', label: 'M8' },
            { value: 'M10', label: 'M10' },
            { value: 'M12', label: 'M12' },
            { value: 'M16', label: 'M16' },
          ]}
          value={v.size}
          onChange={(size) => update({ size })}
        />
      </Field>
      <Field label="長さ (mm)">
        <NumberWithUnit value={v.length} onChange={(length) => update({ length: length ?? 20 })} unit="mm" min={1} max={500} />
      </Field>
      <Field label="頭部形状">
        <ChipSelect
          options={[
            { value: 'flat', label: 'フラット' },
            { value: 'pan', label: 'なべ' },
            { value: 'socket', label: 'ソケット' },
            { value: 'hex', label: '六角' },
            { value: 'button', label: 'ボタン' },
            { value: 'cheese', label: 'チーズ' },
            { value: 'countersunk', label: '皿' },
          ]}
          value={v.head}
          onChange={(head) => update({ head })}
        />
      </Field>
      <Field label="ネジ部">
        <ChipSelect
          options={[
            { value: 'full', label: '全ネジ' },
            { value: 'partial', label: '半ネジ' },
            { value: 'none', label: 'なし' },
          ]}
          value={v.thread}
          onChange={(thread) => update({ thread })}
        />
      </Field>
      <Field label="仕上げ">
        <ChipSelect
          options={[
            { value: 'plain', label: '生地' },
            { value: 'zinc', label: 'ユニクロめっき' },
            { value: 'black_oxide', label: '黒染め' },
            { value: 'stainless', label: 'SUS' },
            { value: 'titanium', label: 'チタン' },
          ]}
          value={v.finish}
          onChange={(finish) => update({ finish })}
        />
      </Field>
    </div>
  );
}

function FixtureForm({
  value,
  onChange,
}: {
  value: MechanicalFixtureDetails | undefined;
  onChange: (next: MechanicalFixtureDetails) => void;
}) {
  const v: MechanicalFixtureDetails = value ?? {
    category: 'mechanical',
    subType: 'fixture',
    fixtureType: 'jig',
    workpieceMax: { length: 200, width: 100, height: 50 },
    clampingMethod: 'toggle',
    baseplateMounting: 'tslot',
    toleranceClass: 'standard',
    hasAlignmentPins: false,
  };
  const update = (next: Partial<MechanicalFixtureDetails>) => onChange({ ...v, ...next });

  return (
    <div className="flex flex-col gap-3">
      <Field label="治具タイプ">
        <ChipSelect
          options={[
            { value: 'jig', label: '治具' },
            { value: 'clamp', label: 'クランプ' },
            { value: 'guide', label: 'ガイド' },
            { value: 'stop', label: 'ストッパー' },
            { value: 'workholding', label: 'ワーク保持' },
          ]}
          value={v.fixtureType}
          onChange={(fixtureType) => update({ fixtureType })}
        />
      </Field>
      <Field label="ワーク最大長 (mm)">
        <NumberWithUnit
          value={v.workpieceMax.length}
          onChange={(length) => update({ workpieceMax: { ...v.workpieceMax, length: length ?? 200 } })}
          unit="mm"
          min={1}
        />
      </Field>
      <Field label="ワーク最大幅 (mm)">
        <NumberWithUnit
          value={v.workpieceMax.width}
          onChange={(width) => update({ workpieceMax: { ...v.workpieceMax, width: width ?? 100 } })}
          unit="mm"
          min={1}
        />
      </Field>
      <Field label="クランプ方式">
        <ChipSelect
          options={[
            { value: 'toggle', label: 'トグル' },
            { value: 'cam', label: 'カム' },
            { value: 'screw', label: 'ネジ' },
            { value: 'pneumatic', label: '空圧' },
            { value: 'magnetic', label: '磁気' },
          ]}
          value={v.clampingMethod}
          onChange={(clampingMethod) => update({ clampingMethod })}
        />
      </Field>
      <Field label="ベース取付">
        <ChipSelect
          options={[
            { value: 'tslot', label: 'Tスロット' },
            { value: 'magnetic', label: '磁気' },
            { value: 'screw_down', label: 'ネジ止め' },
            { value: 'free_standing', label: '自立' },
          ]}
          value={v.baseplateMounting}
          onChange={(baseplateMounting) => update({ baseplateMounting })}
        />
      </Field>
      <Field label="公差クラス">
        <ChipSelect
          options={[
            { value: 'loose', label: 'ゆるい' },
            { value: 'standard', label: '標準' },
            { value: 'tight', label: 'タイト' },
            { value: 'precision', label: '高精度' },
          ]}
          value={v.toleranceClass}
          onChange={(toleranceClass) => update({ toleranceClass })}
        />
      </Field>
      <div className="flex flex-wrap gap-2 pt-1">
        <ToggleChip value={v.hasAlignmentPins} onChange={(hasAlignmentPins) => update({ hasAlignmentPins })} label="位置決めピン" />
      </div>
    </div>
  );
}

// ─── Architecture variants ────────────────────────────────────────────────────

function BuildingForm({
  value,
  onChange,
}: {
  value: ArchitectureBuildingDetails | undefined;
  onChange: (next: ArchitectureBuildingDetails) => void;
}) {
  const v: ArchitectureBuildingDetails = value ?? {
    category: 'architecture',
    subType: 'building',
    buildingType: 'house',
    scaleRatio: '1:100',
    floorCount: 2,
    roofStyle: 'gable',
    showInterior: false,
    showLandscape: false,
  };
  const update = (next: Partial<ArchitectureBuildingDetails>) => onChange({ ...v, ...next });

  return (
    <div className="flex flex-col gap-3">
      <Field label="建物タイプ">
        <ChipSelect
          options={[
            { value: 'house', label: '住宅' },
            { value: 'apartment', label: 'アパート' },
            { value: 'office', label: 'オフィス' },
            { value: 'retail', label: '店舗' },
            { value: 'warehouse', label: '倉庫' },
            { value: 'mixed_use', label: '複合用途' },
          ]}
          value={v.buildingType}
          onChange={(buildingType) => update({ buildingType })}
        />
      </Field>
      <Field label="縮尺">
        <ChipSelect
          options={[
            { value: '1:50', label: '1:50' },
            { value: '1:100', label: '1:100' },
            { value: '1:200', label: '1:200' },
            { value: '1:500', label: '1:500' },
            { value: '1:1000', label: '1:1000' },
          ]}
          value={v.scaleRatio}
          onChange={(scaleRatio) => update({ scaleRatio })}
        />
      </Field>
      <Field label="階数">
        <NumberWithUnit value={v.floorCount} onChange={(floorCount) => update({ floorCount: floorCount ?? 2 })} unit="階" min={1} max={100} />
      </Field>
      <Field label="屋根スタイル">
        <ChipSelect
          options={[
            { value: 'flat', label: '陸屋根' },
            { value: 'gable', label: '切妻' },
            { value: 'hip', label: '寄棟' },
            { value: 'shed', label: '片流れ' },
            { value: 'mansard', label: 'マンサード' },
            { value: 'butterfly', label: 'バタフライ' },
          ]}
          value={v.roofStyle}
          onChange={(roofStyle) => update({ roofStyle })}
        />
      </Field>
      <div className="flex flex-wrap gap-2 pt-1">
        <ToggleChip value={v.showInterior} onChange={(showInterior) => update({ showInterior })} label="内装表示" />
        <ToggleChip value={v.showLandscape} onChange={(showLandscape) => update({ showLandscape })} label="外構表示" />
      </div>
    </div>
  );
}

function InteriorRoomForm({
  value,
  onChange,
}: {
  value: ArchitectureInteriorRoomDetails | undefined;
  onChange: (next: ArchitectureInteriorRoomDetails) => void;
}) {
  const v: ArchitectureInteriorRoomDetails = value ?? {
    category: 'architecture',
    subType: 'interior_room',
    roomType: 'living',
    scaleRatio: '1:50',
    ceilingHeight: 2400,
    showFurniture: true,
    wallTreatment: 'plain',
    showCeiling: false,
  };
  const update = (next: Partial<ArchitectureInteriorRoomDetails>) => onChange({ ...v, ...next });

  return (
    <div className="flex flex-col gap-3">
      <Field label="室タイプ">
        <ChipSelect
          options={[
            { value: 'living', label: 'リビング' },
            { value: 'bedroom', label: '寝室' },
            { value: 'kitchen', label: 'キッチン' },
            { value: 'bath', label: '浴室' },
            { value: 'office', label: 'オフィス' },
            { value: 'retail', label: '店舗' },
          ]}
          value={v.roomType}
          onChange={(roomType) => update({ roomType })}
        />
      </Field>
      <Field label="縮尺">
        <ChipSelect
          options={[
            { value: '1:20', label: '1:20' },
            { value: '1:50', label: '1:50' },
            { value: '1:100', label: '1:100' },
          ]}
          value={v.scaleRatio}
          onChange={(scaleRatio) => update({ scaleRatio })}
        />
      </Field>
      <Field label="天井高 (mm)">
        <NumberWithUnit value={v.ceilingHeight} onChange={(ceilingHeight) => update({ ceilingHeight: ceilingHeight ?? 2400 })} unit="mm" min={1800} max={6000} step={100} />
      </Field>
      <Field label="壁仕上げ">
        <ChipSelect
          options={[
            { value: 'plain', label: '無地' },
            { value: 'wallpaper', label: '壁紙' },
            { value: 'paint', label: '塗装' },
            { value: 'paneling', label: 'パネル' },
            { value: 'tile', label: 'タイル' },
          ]}
          value={v.wallTreatment}
          onChange={(wallTreatment) => update({ wallTreatment })}
        />
      </Field>
      <div className="flex flex-wrap gap-2 pt-1">
        <ToggleChip value={v.showFurniture} onChange={(showFurniture) => update({ showFurniture })} label="家具表示" />
        <ToggleChip value={v.showCeiling} onChange={(showCeiling) => update({ showCeiling })} label="天井表示" />
      </div>
    </div>
  );
}

function SiteForm({
  value,
  onChange,
}: {
  value: ArchitectureSiteDetails | undefined;
  onChange: (next: ArchitectureSiteDetails) => void;
}) {
  const v: ArchitectureSiteDetails = value ?? {
    category: 'architecture',
    subType: 'site',
    siteType: 'suburban',
    scaleRatio: '1:500',
    showTopography: false,
    showVegetation: true,
    showRoads: true,
    buildingCount: 1,
  };
  const update = (next: Partial<ArchitectureSiteDetails>) => onChange({ ...v, ...next });

  return (
    <div className="flex flex-col gap-3">
      <Field label="敷地タイプ">
        <ChipSelect
          options={[
            { value: 'urban', label: '都市部' },
            { value: 'suburban', label: '郊外' },
            { value: 'rural', label: '農村' },
            { value: 'park', label: '公園' },
            { value: 'campus', label: 'キャンパス' },
          ]}
          value={v.siteType}
          onChange={(siteType) => update({ siteType })}
        />
      </Field>
      <Field label="縮尺">
        <ChipSelect
          options={[
            { value: '1:200', label: '1:200' },
            { value: '1:500', label: '1:500' },
            { value: '1:1000', label: '1:1000' },
            { value: '1:2000', label: '1:2000' },
          ]}
          value={v.scaleRatio}
          onChange={(scaleRatio) => update({ scaleRatio })}
        />
      </Field>
      <Field label="建物棟数">
        <NumberWithUnit value={v.buildingCount} onChange={(buildingCount) => update({ buildingCount: buildingCount ?? 1 })} unit="棟" min={1} max={100} />
      </Field>
      <div className="flex flex-wrap gap-2 pt-1">
        <ToggleChip value={v.showTopography} onChange={(showTopography) => update({ showTopography })} label="地形表示" />
        <ToggleChip value={v.showVegetation} onChange={(showVegetation) => update({ showVegetation })} label="植栽表示" />
        <ToggleChip value={v.showRoads} onChange={(showRoads) => update({ showRoads })} label="道路表示" />
      </div>
    </div>
  );
}

function FacadeDetailForm({
  value,
  onChange,
}: {
  value: ArchitectureFacadeDetailDetails | undefined;
  onChange: (next: ArchitectureFacadeDetailDetails) => void;
}) {
  const v: ArchitectureFacadeDetailDetails = value ?? {
    category: 'architecture',
    subType: 'facade_detail',
    detailType: 'window',
    scaleRatio: '1:10',
    material: 'masonry',
    showSection: true,
    showLayers: false,
  };
  const update = (next: Partial<ArchitectureFacadeDetailDetails>) => onChange({ ...v, ...next });

  return (
    <div className="flex flex-col gap-3">
      <Field label="詳細タイプ">
        <ChipSelect
          options={[
            { value: 'window', label: '窓' },
            { value: 'door', label: '扉' },
            { value: 'cornice', label: 'コーニス' },
            { value: 'parapet', label: 'パラペット' },
            { value: 'curtain_wall', label: 'カーテンウォール' },
          ]}
          value={v.detailType}
          onChange={(detailType) => update({ detailType })}
        />
      </Field>
      <Field label="縮尺">
        <ChipSelect
          options={[
            { value: '1:5', label: '1:5' },
            { value: '1:10', label: '1:10' },
            { value: '1:20', label: '1:20' },
            { value: '1:50', label: '1:50' },
          ]}
          value={v.scaleRatio}
          onChange={(scaleRatio) => update({ scaleRatio })}
        />
      </Field>
      <Field label="素材">
        <ChipSelect
          options={[
            { value: 'masonry', label: '組積造' },
            { value: 'concrete', label: 'コンクリート' },
            { value: 'metal', label: '金属' },
            { value: 'glass', label: 'ガラス' },
            { value: 'wood', label: '木材' },
            { value: 'composite', label: '複合' },
          ]}
          value={v.material}
          onChange={(material) => update({ material })}
        />
      </Field>
      <div className="flex flex-wrap gap-2 pt-1">
        <ToggleChip value={v.showSection} onChange={(showSection) => update({ showSection })} label="断面表示" />
        <ToggleChip value={v.showLayers} onChange={(showLayers) => update({ showLayers })} label="層構成表示" />
      </div>
    </div>
  );
}

// ─── Printing variants ────────────────────────────────────────────────────────

function DecorativeForm({
  value,
  onChange,
}: {
  value: PrintingDecorativeDetails | undefined;
  onChange: (next: PrintingDecorativeDetails) => void;
}) {
  const v: PrintingDecorativeDetails = value ?? {
    category: 'printing',
    subType: 'decorative',
    itemType: 'vase',
    printer: 'FDM',
    layerHeight: 0.2,
    shellThickness: 1.2,
    supportPolicy: 'avoid',
    vaseModeCompatible: false,
  };
  const update = (next: Partial<PrintingDecorativeDetails>) => onChange({ ...v, ...next });

  return (
    <div className="flex flex-col gap-3">
      <Field label="アイテムタイプ">
        <ChipSelect
          options={[
            { value: 'vase', label: '花瓶' },
            { value: 'planter', label: 'プランター' },
            { value: 'art_object', label: 'アートオブジェ' },
            { value: 'ornament', label: 'オーナメント' },
          ]}
          value={v.itemType}
          onChange={(itemType) => update({ itemType })}
        />
      </Field>
      <Field label="プリンター">
        <ChipSelect
          options={[
            { value: 'FDM', label: 'FDM' },
            { value: 'SLA', label: 'SLA' },
            { value: 'SLS', label: 'SLS' },
          ]}
          value={v.printer}
          onChange={(printer) => update({ printer })}
        />
      </Field>
      <Field label="積層ピッチ (mm)">
        <NumberWithUnit value={v.layerHeight} onChange={(layerHeight) => update({ layerHeight: layerHeight ?? 0.2 })} unit="mm" min={0.05} max={0.4} step={0.05} />
      </Field>
      <Field label="シェル厚 (mm)">
        <NumberWithUnit value={v.shellThickness} onChange={(shellThickness) => update({ shellThickness: shellThickness ?? 1.2 })} unit="mm" min={0.4} max={5} step={0.4} />
      </Field>
      <Field label="サポート方針">
        <ChipSelect
          options={[
            { value: 'avoid', label: '回避' },
            { value: 'allow_minimal', label: '最小限' },
            { value: 'allow_full', label: '全面許可' },
          ]}
          value={v.supportPolicy}
          onChange={(supportPolicy) => update({ supportPolicy })}
        />
      </Field>
      <div className="flex flex-wrap gap-2 pt-1">
        <ToggleChip value={v.vaseModeCompatible} onChange={(vaseModeCompatible) => update({ vaseModeCompatible })} label="花瓶モード対応" />
      </div>
    </div>
  );
}

function FunctionalForm({
  value,
  onChange,
}: {
  value: PrintingFunctionalDetails | undefined;
  onChange: (next: PrintingFunctionalDetails) => void;
}) {
  const v: PrintingFunctionalDetails = value ?? {
    category: 'printing',
    subType: 'functional',
    functionalRole: 'bracket',
    printer: 'FDM',
    infillPercent: 40,
    shellThickness: 1.6,
    supportPolicy: 'allow_minimal',
    toleranceClass: 'standard',
    insertType: 'none',
  };
  const update = (next: Partial<PrintingFunctionalDetails>) => onChange({ ...v, ...next });

  return (
    <div className="flex flex-col gap-3">
      <Field label="機能的役割">
        <ChipSelect
          options={[
            { value: 'mount', label: 'マウント' },
            { value: 'adapter', label: 'アダプター' },
            { value: 'tool', label: '工具' },
            { value: 'hook', label: 'フック' },
            { value: 'bracket', label: 'ブラケット' },
            { value: 'gear', label: '歯車' },
          ]}
          value={v.functionalRole}
          onChange={(functionalRole) => update({ functionalRole })}
        />
      </Field>
      <Field label="プリンター">
        <ChipSelect
          options={[
            { value: 'FDM', label: 'FDM' },
            { value: 'SLA', label: 'SLA' },
            { value: 'SLS', label: 'SLS' },
          ]}
          value={v.printer}
          onChange={(printer) => update({ printer })}
        />
      </Field>
      <Field label="インフィル (%)">
        <NumberWithUnit value={v.infillPercent} onChange={(infillPercent) => update({ infillPercent: infillPercent ?? 40 })} unit="%" min={10} max={100} step={5} />
      </Field>
      <Field label="シェル厚 (mm)">
        <NumberWithUnit value={v.shellThickness} onChange={(shellThickness) => update({ shellThickness: shellThickness ?? 1.6 })} unit="mm" min={0.4} max={5} step={0.4} />
      </Field>
      <Field label="サポート方針">
        <ChipSelect
          options={[
            { value: 'avoid', label: '回避' },
            { value: 'allow_minimal', label: '最小限' },
            { value: 'allow_full', label: '全面許可' },
          ]}
          value={v.supportPolicy}
          onChange={(supportPolicy) => update({ supportPolicy })}
        />
      </Field>
      <Field label="公差クラス">
        <ChipSelect
          options={[
            { value: 'loose', label: 'ゆるい' },
            { value: 'standard', label: '標準' },
            { value: 'tight', label: 'タイト' },
          ]}
          value={v.toleranceClass}
          onChange={(toleranceClass) => update({ toleranceClass })}
        />
      </Field>
      <Field label="インサートタイプ">
        <ChipSelect
          options={[
            { value: 'none', label: 'なし' },
            { value: 'heat_set', label: 'ヒートセット' },
            { value: 'press_fit', label: '圧入' },
            { value: 'self_tapping', label: 'セルフタッピング' },
          ]}
          value={v.insertType}
          onChange={(insertType) => update({ insertType })}
        />
      </Field>
    </div>
  );
}

function ReplacementForm({
  value,
  onChange,
}: {
  value: PrintingReplacementDetails | undefined;
  onChange: (next: PrintingReplacementDetails) => void;
}) {
  const v: PrintingReplacementDetails = value ?? {
    category: 'printing',
    subType: 'replacement',
    replacingType: 'knob',
    printer: 'FDM',
    matchOriginalDimensions: true,
    toleranceClass: 'standard',
    fastenerInterface: 'none',
    reinforcement: 'none',
  };
  const update = (next: Partial<PrintingReplacementDetails>) => onChange({ ...v, ...next });

  return (
    <div className="flex flex-col gap-3">
      <Field label="交換部品タイプ">
        <ChipSelect
          options={[
            { value: 'knob', label: 'ノブ' },
            { value: 'clip', label: 'クリップ' },
            { value: 'gear', label: '歯車' },
            { value: 'bracket', label: 'ブラケット' },
            { value: 'cover', label: 'カバー' },
            { value: 'connector', label: 'コネクター' },
          ]}
          value={v.replacingType}
          onChange={(replacingType) => update({ replacingType })}
        />
      </Field>
      <Field label="プリンター">
        <ChipSelect
          options={[
            { value: 'FDM', label: 'FDM' },
            { value: 'SLA', label: 'SLA' },
          ]}
          value={v.printer}
          onChange={(printer) => update({ printer })}
        />
      </Field>
      <Field label="公差クラス">
        <ChipSelect
          options={[
            { value: 'loose', label: 'ゆるい' },
            { value: 'standard', label: '標準' },
            { value: 'tight', label: 'タイト' },
          ]}
          value={v.toleranceClass}
          onChange={(toleranceClass) => update({ toleranceClass })}
        />
      </Field>
      <Field label="ファスナーインターフェース">
        <ChipSelect
          options={[
            { value: 'none', label: 'なし' },
            { value: 'screw_M3', label: 'M3ネジ' },
            { value: 'screw_M4', label: 'M4ネジ' },
            { value: 'screw_M5', label: 'M5ネジ' },
            { value: 'snap_fit', label: 'スナップフィット' },
          ]}
          value={v.fastenerInterface}
          onChange={(fastenerInterface) => update({ fastenerInterface })}
        />
      </Field>
      <Field label="補強">
        <ChipSelect
          options={[
            { value: 'none', label: 'なし' },
            { value: 'thicker_walls', label: '肉厚増加' },
            { value: 'ribs', label: 'リブ' },
            { value: 'metal_insert', label: '金属インサート' },
          ]}
          value={v.reinforcement}
          onChange={(reinforcement) => update({ reinforcement })}
        />
      </Field>
      <div className="flex flex-wrap gap-2 pt-1">
        <ToggleChip value={v.matchOriginalDimensions} onChange={(matchOriginalDimensions) => update({ matchOriginalDimensions })} label="寸法一致" />
      </div>
    </div>
  );
}

function MiniatureForm({
  value,
  onChange,
}: {
  value: PrintingMiniatureDetails | undefined;
  onChange: (next: PrintingMiniatureDetails) => void;
}) {
  const v: PrintingMiniatureDetails = value ?? {
    category: 'printing',
    subType: 'miniature',
    miniType: 'figure',
    scale: '28mm',
    printer: 'SLA',
    layerHeight: 0.05,
    detailLevel: 'high',
    basePresent: true,
  };
  const update = (next: Partial<PrintingMiniatureDetails>) => onChange({ ...v, ...next });

  return (
    <div className="flex flex-col gap-3">
      <Field label="ミニチュアタイプ">
        <ChipSelect
          options={[
            { value: 'figure', label: 'フィギュア' },
            { value: 'terrain', label: '地形' },
            { value: 'vehicle', label: '乗り物' },
            { value: 'building_kit', label: '建物キット' },
            { value: 'modular_tile', label: 'モジュラータイル' },
          ]}
          value={v.miniType}
          onChange={(miniType) => update({ miniType })}
        />
      </Field>
      <Field label="スケール">
        <ChipSelect
          options={[
            { value: '1:35', label: '1:35' },
            { value: '1:48', label: '1:48' },
            { value: '1:72', label: '1:72' },
            { value: '1:100', label: '1:100' },
            { value: '28mm', label: '28mm' },
            { value: '32mm', label: '32mm' },
          ]}
          value={v.scale}
          onChange={(scale) => update({ scale })}
        />
      </Field>
      <Field label="プリンター">
        <ChipSelect
          options={[
            { value: 'FDM', label: 'FDM' },
            { value: 'SLA', label: 'SLA' },
          ]}
          value={v.printer}
          onChange={(printer) => update({ printer })}
        />
      </Field>
      <Field label="積層ピッチ (mm)">
        <NumberWithUnit value={v.layerHeight} onChange={(layerHeight) => update({ layerHeight: layerHeight ?? 0.05 })} unit="mm" min={0.01} max={0.3} step={0.01} />
      </Field>
      <Field label="細部レベル">
        <ChipSelect
          options={[
            { value: 'low', label: '低' },
            { value: 'medium', label: '中' },
            { value: 'high', label: '高' },
            { value: 'ultra', label: '超高' },
          ]}
          value={v.detailLevel}
          onChange={(detailLevel) => update({ detailLevel })}
        />
      </Field>
      <div className="flex flex-wrap gap-2 pt-1">
        <ToggleChip value={v.basePresent} onChange={(basePresent) => update({ basePresent })} label="ベースあり" />
      </div>
    </div>
  );
}

function EnclosureForm({
  value,
  onChange,
}: {
  value: PrintingEnclosureDetails | undefined;
  onChange: (next: PrintingEnclosureDetails) => void;
}) {
  const v: PrintingEnclosureDetails = value ?? {
    category: 'printing',
    subType: 'enclosure',
    enclosureFor: 'electronics',
    printer: 'FDM',
    closure: 'snap_fit',
    insertType: 'none',
    hasVents: false,
    hasCableEntry: true,
    ipRating: 'none',
  };
  const update = (next: Partial<PrintingEnclosureDetails>) => onChange({ ...v, ...next });

  return (
    <div className="flex flex-col gap-3">
      <Field label="用途">
        <ChipSelect
          options={[
            { value: 'electronics', label: '電子機器' },
            { value: 'storage', label: '収納' },
            { value: 'food_safe', label: '食品対応' },
            { value: 'outdoor', label: '屋外' },
            { value: 'general', label: '汎用' },
          ]}
          value={v.enclosureFor}
          onChange={(enclosureFor) => update({ enclosureFor })}
        />
      </Field>
      <Field label="プリンター">
        <ChipSelect
          options={[
            { value: 'FDM', label: 'FDM' },
            { value: 'SLA', label: 'SLA' },
          ]}
          value={v.printer}
          onChange={(printer) => update({ printer })}
        />
      </Field>
      <Field label="蓋の固定">
        <ChipSelect
          options={[
            { value: 'snap_fit', label: 'スナップフィット' },
            { value: 'screw', label: 'ネジ' },
            { value: 'magnet', label: '磁石' },
            { value: 'press_fit', label: '圧入' },
            { value: 'hinged_lid', label: 'ヒンジ蓋' },
          ]}
          value={v.closure}
          onChange={(closure) => update({ closure })}
        />
      </Field>
      <Field label="インサートタイプ">
        <ChipSelect
          options={[
            { value: 'none', label: 'なし' },
            { value: 'heat_set_M2', label: 'ヒートセット M2' },
            { value: 'heat_set_M3', label: 'ヒートセット M3' },
            { value: 'heat_set_M4', label: 'ヒートセット M4' },
          ]}
          value={v.insertType}
          onChange={(insertType) => update({ insertType })}
        />
      </Field>
      <Field label="IP等級">
        <ChipSelect
          options={[
            { value: 'none', label: 'なし' },
            { value: 'IP54', label: 'IP54' },
            { value: 'IP65', label: 'IP65' },
          ]}
          value={v.ipRating}
          onChange={(ipRating) => update({ ipRating })}
        />
      </Field>
      <div className="flex flex-wrap gap-2 pt-1">
        <ToggleChip value={v.hasVents} onChange={(hasVents) => update({ hasVents })} label="通気口あり" />
        <ToggleChip value={v.hasCableEntry} onChange={(hasCableEntry) => update({ hasCableEntry })} label="ケーブル引出し" />
      </div>
    </div>
  );
}
